import { Fragment } from 'react'

import { EmptyState, Space, Table, Text, TextLoader } from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import messages from './messages'

export interface IBillMatchedItemsTableProps {
  matches: ReconcileMatch[]
  /** metadata.summary.total_amount — "Total cost" line (BHV-050) */
  totalCost: Nullable<number>
  currencyName: string
  isLoading: boolean
}

// Read-only bill representation of the session's confirmed matches (BHV-050).
// "Department" is a display label; the entity props are secondary/primary
// category (ADR-7/8). Uses the DS Table block so allocation sub-rows can be
// full-width secondary rows.
export const BillMatchedItemsTable = ({
  matches,
  totalCost,
  currencyName,
  isLoading,
}: IBillMatchedItemsTableProps) => {
  const intl = useProcurifyIntl()

  if (isLoading) {
    return <TextLoader />
  }

  if (matches.length === 0) {
    return <EmptyState header={intl.formatMessage(messages.emptyStateHeader)} />
  }

  const getDepartmentLabel = (item: CreditCardItem): string => {
    if (item.costAllocations.length === 0) return '-'
    if (item.costAllocations.length > 1) {
      return intl.formatMessage(messages.multipleLabel)
    }
    return item.costAllocations[0].secondaryCategory.name
  }

  // Flatten each match's credit-card items into individual bill rows.
  const rows = matches.reduce<
    { match: ReconcileMatch; item: CreditCardItem }[]
  >((acc, match) => {
    match.creditcardItems.forEach((item) => acc.push({ match, item }))
    return acc
  }, [])

  return (
    <PaginationControls id='ap-reconcile-matched'>
      <Space>
        <Table aria-label={intl.formatMessage(messages.tableAriaLabel)}>
          <Table.Head>
            <Table.Row>
              <Table.Cell>{intl.formatMessage(messages.refColumn)}</Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.descriptionColumn)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.departmentColumn)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.accountColumn)}
              </Table.Cell>
              <Table.Cell align='right'>
                {intl.formatMessage(messages.matchesColumn)}
              </Table.Cell>
              <Table.Cell>{intl.formatMessage(messages.dateColumn)}</Table.Cell>
              <Table.Cell align='right'>
                {intl.formatMessage(messages.lineCostColumn)}
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {rows.map(({ match, item }) => (
              <Fragment key={`${match.id}-${item.id}`}>
                <Table.Row>
                  <Table.Cell nowrap>
                    <Text bold autoMargin={false}>
                      {item.refLabel ?? '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{item.descriptionLabel}</Table.Cell>
                  <Table.Cell>{getDepartmentLabel(item)}</Table.Cell>
                  <Table.Cell>{item.accountCodeLabel}</Table.Cell>
                  <Table.Cell align='right'>
                    {match.statementItemCount}
                  </Table.Cell>
                  <Table.Cell nowrap>
                    <FormattedShortDate date={item.receivedOn} />
                  </Table.Cell>
                  <Table.Cell align='right' nowrap>
                    {item.totalCost.toFixed(2)} {item.currency.name}
                  </Table.Cell>
                </Table.Row>

                {item.costAllocations.length > 1 &&
                  item.costAllocations.map((allocation) => (
                    <Table.Row key={`${item.id}-${allocation.budgetId}`}>
                      <Table.Cell />
                      <Table.Cell />
                      <Table.Cell>
                        <Text color='textSecondary' autoMargin={false}>
                          {allocation.secondaryCategory.name}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text color='textSecondary' autoMargin={false}>
                          {allocation.accountCodeLabel}
                        </Text>
                      </Table.Cell>
                      <Table.Cell />
                      <Table.Cell />
                      <Table.Cell align='right' nowrap>
                        <Text color='textSecondary' autoMargin={false}>
                          {allocation.amountValue.toFixed(2)}{' '}
                          {item.currency.name}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Fragment>
            ))}
          </Table.Body>
        </Table>

        <Space direction='horizontal' justify='end'>
          <PaginationControls.Page />
        </Space>

        <Space direction='horizontal' justify='end'>
          <Text bold autoMargin={false}>
            {intl.formatMessage(messages.totalCostLabel, {
              amount: (totalCost ?? 0).toFixed(2),
              currency: currencyName,
            })}
          </Text>
        </Space>
      </Space>
    </PaginationControls>
  )
}

BillMatchedItemsTable.displayName = 'BillMatchedItemsTable'
